import { format, isValid, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import { useStyles } from 'react-native-unistyles';

import { formatInZone } from '@/core';
import { Icon } from '@/ui/icon';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Input } from '@/ui/input';

const DATETIME_FORMAT = 'MM/dd/yyyy h:mm a';

type DatetimeInputProps = {
  label: string;
  value?: Date;
  onChange: (date: Date) => void;
  onCalendarPress: () => void;
  minDate?: Date;
  timeZone?: string;
  containerStyle?: any;
};

// Text field for typing a date and time. The calendar only opens from the icon.
export const DatetimeInput = ({
  label,
  value,
  onChange,
  onCalendarPress,
  minDate,
  timeZone,
  containerStyle,
}: DatetimeInputProps) => {
  const { theme } = useStyles();
  const [text, setText] = useState(value ? format(value, DATETIME_FORMAT) : '');
  const [error, setError] = useState<string>();

  useEffect(() => {
    setText(value ? format(value, DATETIME_FORMAT) : '');
    setError(undefined);
  }, [value]);

  const commit = () => {
    if (!text.trim()) {
      setError(undefined);
      return;
    }

    // "08/31/26" is year 0026 under yyyy. Treat a 2-digit year as 20xx.
    const normalized = text.trim().replace(
      /^(\d{1,2}\/\d{1,2}\/)(\d{2})(\s+)/,
      (_, date, year, space) => `${date}20${year}${space}`
    );
    const parsed = parse(normalized, DATETIME_FORMAT, new Date());

    if (!isValid(parsed)) {
      setError('Use MM/DD/YYYY h:mm AM/PM');
      return;
    }

    if (minDate && parsed < minDate) {
      setError(`Must be on or after ${format(minDate, DATETIME_FORMAT)}`);
      return;
    }

    setError(undefined);
    onChange(parsed);
  };

  return (
    <Input
      label={label}
      value={text}
      placeholder="MM/DD/YYYY h:mm AM/PM"
      onChangeText={(t) => {
        setText(t);
        setError(undefined);
      }}
      onBlur={commit}
      onSubmitEditing={commit}
      error={error}
      infoText={
        value && timeZone ? formatInZone(value, timeZone, 'zzz') : undefined
      }
      rightIcon={
        <Icon
          type="naked"
          icon={<CalendarIcon />}
          fillColor={theme.colors.icon.color}
          width={22}
          height={22}
        />
      }
      onRightIconPress={onCalendarPress}
      containerStyle={containerStyle}
    />
  );
};
